import { createI18n } from 'vue-i18n';

const defaultLocale = import.meta.env.VITE_DEFAULT_LOCALE || 'en';
const availableLocales = ['en', 'bg', 'ar', 'he', 'el'];

/**
 * Automatically load all locale files from locales/{lang}/*.json
 * and merge them into a single messages object per language
 */
function loadLocaleMessages() {
  // Use Vite's import.meta.glob to load all JSON files
  const localeModules = import.meta.glob('./locales/*/*.json', { eager: true });

  const messages = {};

  // Process each loaded file
  for (const path in localeModules) {
    // Extract language code and filename
    // Example path: './locales/en/common.json'
    const match = path.match(/\.\/locales\/([a-z]{2})\/(.+)\.json$/);

    if (match) {
      const locale = match[1];  // 'en'
      const fileName = match[2]; // 'common'

      // Initialize locale object if not exists
      if (!messages[locale]) {
        messages[locale] = {};
      }

      // Merge the module's content into the locale
      // This preserves the nested structure from each file
      Object.assign(messages[locale], localeModules[path].default || localeModules[path]);

      console.log(`✓ Loaded ${locale}/${fileName}.json`);
    }
  }

  return messages;
}

// Load all messages
const messages = loadLocaleMessages();

// Validate that we have at least the default locale
if (!messages[defaultLocale]) {
  console.error(`Default locale "${defaultLocale}" not found!`);
}

// Get stored locale or use default
let storedLocale = localStorage.getItem('locale') || defaultLocale;

// Validate stored locale
if (!availableLocales.includes(storedLocale)) {
  console.warn(`Invalid locale "${storedLocale}", falling back to "${defaultLocale}"`);
  storedLocale = defaultLocale;
  localStorage.setItem('locale', defaultLocale);
}

const i18n = createI18n({
  legacy: false,
  locale: storedLocale,
  fallbackLocale: 'en',
  messages,
  // Suppress warnings in development
  silentTranslationWarn: false,
  silentFallbackWarn: false,
  // Handle missing keys gracefully
  missing: (locale, key) => {
    if (import.meta.env.DEV) {
      console.warn(`Missing translation: [${locale}] ${key}`);
    }
    return key;
  }
});

// Export helper function to get available locales
export const getAvailableLocales = () => availableLocales;

// Export helper to switch locale
export const switchLocale = (newLocale) => {
  if (availableLocales.includes(newLocale)) {
    i18n.global.locale.value = newLocale;
    localStorage.setItem('locale', newLocale);
    return true;
  }
  return false;
};

export default i18n;
